import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import $ from 'jquery';
import * as PropTypes from 'prop-types';
import React, {RefObject} from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import hoverAndActiveStyles from 'styles/hoverAndActiveStyles';
import DataTestAttrsType from 'test/lib/cypress/DataTestAttrsType';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import InlineBanner, {InlineBannerProps} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER, WHITE, GRAY_DARK} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import findAncestor from 'util/findAncestor';
import * as keyboard from 'util/Keyboard';
import pick from 'util/pick';
import {BasicEvent, ElementKeyboardEvent} from 'util/Types';

export enum ModalBodyColor {
  WHITE = 'WHITE',
  GRAY_LIGHTER = 'GRAY_LIGHTER',
  TRANSPARENT = 'TRANSPARENT',
}

type KeysOfStyles = keyof typeof styles;
type ValuesOfStyles = (typeof styles)[KeysOfStyles];

function getBodyBackgroundStyle(color: ModalBodyColor | undefined): ValuesOfStyles {
  switch (color) {
    case ModalBodyColor.WHITE:
      return styles.whiteBg;
    case ModalBodyColor.GRAY_LIGHTER:
      return styles.grayLighterBg;
    default:
      return styles.transparentBg;
  }
}

export interface ModalButton {
  text?: string;
  key?: string;
  disabled?: boolean;
  submit?: boolean;
  cancel?: boolean;
  pullLeft?: boolean;
  asLink?: boolean;
  faded?: boolean;
  className?: string;
  tooltipTitle?: string | null;
  // Note that these are legacy styles that we should eventually remove.
  style?: 'default' | 'primary' | 'success' | 'danger' | ButtonStyle | undefined;
  onClick?: (e?: BasicEvent) => void;
  dataTestAttrs?: DataTestAttrs;

  // Set to allow clicking on a link to submit a non-GET request (currently only POST supported).
  // asLink must be true.
  // See client/App.ts:_bindGlobalListeners() for more detail.
  dataMethod?: string;
  href?: string;
}

export interface SharedModalProps {
  cidOverride?: string;
  className?: string | null;
  title?: React.ReactNode;
  titleIcon?: BenchlingIconName | null;
  buttons?: Array<ModalButton> | null;
  closeable?: boolean;
  closeableOnBackgroundClick?: boolean;
  closeableOnEscapeKey?: boolean;
  footer?: React.ReactNode;
  footerPullRight?: boolean;
  scrollable?: boolean;
  extraLarge?: boolean;
  large?: boolean;
  mediumLarge?: boolean;
  medium?: boolean;
  small?: boolean;
  /** Overrides any other size props passed in except for extraLarge, which is wider than fullWidth */
  fullWidth?: boolean;
  fullHeight?: boolean;
  expandHeight?: boolean;
  bodyPadded?: boolean;
  suppressHeader?: boolean;
  wrapInForm?: boolean;
  // TODO(T6278): Merge bootstrap modal styling into new modal styling
  enableRedesign?: boolean;
  showFooterBorder?: boolean;
  showHeaderBorder?: boolean;
  bannerProps?: InlineBannerProps | null;
  dataTestAttrs?: DataTestAttrs;
  additionalHeaderIcons?: Array<JSX.Element> | null;
  flexBody?: boolean;
  flexContent?: boolean;
  centerBody?: boolean;
  modalBodyBgColor?: ModalBodyColor;
  /**
   * If the modal needs to be rendered over a tooltip that opened it, set zIndex to 1060. However, this has
   * potential negative consequences if overused; for example, tooltips opened from within the modal (like
   * from a chip rendered in the modal) may be rendered behind the modal. So a good rule of thumb is only
   * to override this prop if you are confident your modal will never trigger any tooltips and it is needed
   * for the modal to display properly.
   *
   * @default 1040, set from `modals.less`
   */
  zIndex?: 1040 | 1060;
}

export interface ModalProps extends SharedModalProps {
  children: React.ReactNode;
  onRequestClose: () => void;
  onRequestSubmit?: (() => void) | null;
  onRequestSignout?: () => void;
  modalKey?: string;
  modalRef?: RefObject<HTMLDivElement>;
}

interface ModalState {
  // ID to be surfaced via the `data-modal-cid` html attribute to support uniquely selecting the DOM node
  cid: string;
}

class Modal extends React.Component<ModalProps, ModalState> {
  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    title: PropTypes.node,
    titleIcon: PropTypes.string,
    onRequestClose: PropTypes.func.isRequired,
    onRequestSubmit: PropTypes.func,
    onRequestSignout: PropTypes.func,
    buttons: PropTypes.array,
    closeable: PropTypes.bool.isRequired,
    closeableOnBackgroundClick: PropTypes.bool.isRequired,
    closeableOnEscapeKey: PropTypes.bool.isRequired,
    footer: PropTypes.node,
    footerPullRight: PropTypes.bool,
    scrollable: PropTypes.bool,
    extraLarge: PropTypes.bool,
    large: PropTypes.bool.isRequired,
    mediumLarge: PropTypes.bool.isRequired,
    medium: PropTypes.bool.isRequired,
    small: PropTypes.bool.isRequired,
    fullWidth: PropTypes.bool.isRequired,
    fullHeight: PropTypes.bool.isRequired,
    expandHeight: PropTypes.bool.isRequired,
    bodyPadded: PropTypes.bool.isRequired,
    suppressHeader: PropTypes.bool.isRequired,
    wrapInForm: PropTypes.bool.isRequired,
    // TODO(T6278): Merge bootstrap modal styling into new modal styling
    enableRedesign: PropTypes.bool.isRequired,
    showFooterBorder: PropTypes.bool.isRequired,
    showHeaderBorder: PropTypes.bool.isRequired,
    bannerProps: PropTypes.object,
    dataTestAttrs: DataTestAttrsType,
    additionalHeaderIcons: PropTypes.array,
    flexBody: PropTypes.bool,
    flexContent: PropTypes.bool,
    centerBody: PropTypes.bool,
    modalBodyBgColor: PropTypes.string,
    zIndex: PropTypes.number,
    modalRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({current: PropTypes.instanceOf(Element)}),
    ]),
  };

  static defaultProps = {
    closeable: true,
    closeableOnBackgroundClick: false,
    closeableOnEscapeKey: true,
    large: false,
    mediumLarge: false,
    medium: false,
    small: false,
    fullWidth: false,
    fullHeight: false,
    bodyPadded: true,
    suppressHeader: false,
    wrapInForm: true,
    enableRedesign: true,
    showFooterBorder: true,
    showHeaderBorder: true,
    flexBody: false,
    flexContent: false,
    centerBody: false,
    modalBodyBgColor: ModalBodyColor.WHITE,
    // Allow modals to expand height and be scrollable by default, this fixes issues with nested Floats
    // with inputs that can't be focused otherwise
    expandHeight: true,
    scrollable: true,
  };

  constructor(props: ModalProps) {
    super(props);
    this.state = {cid: props.cidOverride ?? _.uniqueId('modal')};
  }

  modalRef = this.props.modalRef ?? React.createRef<HTMLDivElement>();
  modalHeadingRef = React.createRef<HTMLDivElement>();

  override componentDidMount(): void {
    $(ReactDOM.findDOMNode(this.modalRef.current)!)
      .modal(this.getModalOptions())
      .on('hide.bs.modal', (e) => {
        e.preventDefault();
        this.props.onRequestClose();
      });

    // For accessibility: we manually assign focus inside the modal if no element has specifically
    // been focused (via `autoFocus` or `.focus()`). This ensures that:
    //   * the browser will blur the active UI element that triggered the modal (button, input)
    //   * it will allow screen readers to know the boundary within which the tab key will operate
    // We choose the heading as the element to focus, specifically because it's not interactive (and
    // therefore will not trigger unexpected behavior if the user quickly hits SPACE or ENTER).
    // Modals with `suppressHeader=true` are on their own!
    if (
      this.modalHeadingRef.current &&
      !findAncestor(document.activeElement, (node) => node === this.modalRef.current)
    ) {
      this.modalHeadingRef.current.focus();
    }
  }

  override componentDidUpdate(prevProps: ModalProps): void {
    const modalOptionProps = (
      props: ModalProps
    ): Pick<ModalProps, 'closeable' | 'closeableOnBackgroundClick' | 'closeableOnEscapeKey'> =>
      pick(props, 'closeable', 'closeableOnBackgroundClick', 'closeableOnEscapeKey');
    if (isEqual(modalOptionProps(this.props), modalOptionProps(prevProps))) {
      Object.assign(
        $(ReactDOM.findDOMNode(this.modalRef.current)!).data('bs.modal').options,
        this.getModalOptions()
      );
    }
  }

  override componentWillUnmount(): void {
    $(ReactDOM.findDOMNode(this.modalRef.current)!).off('hide.bs.modal').modal('hide');
  }

  private getModalOptions(): ModalOptions {
    return {
      backdrop: !this.props.closeable || !this.props.closeableOnBackgroundClick ? 'static' : undefined,
      keyboard: !this.props.closeable || !this.props.closeableOnEscapeKey ? false : undefined,
    };
  }

  private getSubmitButtons(): Array<ModalButton> {
    return (this.props.buttons || []).filter((button) => Boolean(button.submit));
  }

  private isSubmitEnabled(): boolean {
    return this.getSubmitButtons().some((button) => !button.disabled);
  }

  private handleSubmit = (e: BasicEvent): void => {
    e.preventDefault();
    if (typeof this.props.onRequestSubmit === 'function' && this.isSubmitEnabled()) {
      this.props.onRequestSubmit();
    }
  };

  private handleOnKeyDown = (e: ElementKeyboardEvent): void => {
    if (!this.props.buttons) {
      return;
    }
    if (keyboard.isOSCtrlKey(e) && keyboard.isEnterKey(e)) {
      this.handleSubmit(e);
    }
  };

  private renderModalFooterButtons(): Array<JSX.Element> {
    return this.props.buttons!.map((button, i) => {
      const {text, submit, cancel, pullLeft, asLink, faded, className, tooltipTitle} = button;
      let {style, disabled, onClick, key} = button;
      disabled = disabled === true;
      assertAndContinue(
        cancel != null || submit != null || onClick != null || disabled,
        'Modal button must be cancel or submit or have onClick or be disabled'
      );
      if (style == null) {
        if (cancel) {
          style = this.props.enableRedesign ? ButtonStyle.TERTIARY_GRAY : 'default';
        } else if (submit) {
          style = this.props.enableRedesign ? ButtonStyle.PRIMARY : 'success';
        } else {
          style = this.props.enableRedesign ? ButtonStyle.TERTIARY_GRAY : 'default';
        }
      }
      const type = submit ? 'submit' : 'button';
      // Button key needs to include type so that submit and non-submit buttons never reuse the same node.
      // Otherwise, we encounter bug where
      // 1) "previous" button (0th button, non-submit) is clicked,
      // 2) React re-renders and new 0th button is a submit button,
      // 3) click event reaches parent form, and since clicked button is now a submit button, the form's
      //    submit event is erroneously triggered.
      if (onClick == null && cancel) {
        onClick = this.props.onRequestClose;
      }

      if (submit) {
        // Only one of these should be provided:
        // * onRequestSubmit
        // * an "onClick" handler on the submit button
        assertAndContinue(
          !(onClick != null && this.props.onRequestSubmit != null),
          'You should not provide both a submit button onClick handler and an "onRequestSubmit" handler'
        );

        assertAndContinue(!cancel, 'A single modal button cannot be both submit and cancel');
      }

      if (key == null) {
        key = `btn-${type}-${i}`;
      }
      const baseClassName = classNames(className, {
        disabled,
        'is-disabled': this.props.enableRedesign && disabled,
        'pull-left': pullLeft,
      });

      if (!submit && !cancel && onClick != null) {
        const _onClick = onClick;
        onClick = (e) => {
          if (disabled) {
            return;
          }

          if (e == null) {
            _onClick(e);
            return;
          }

          // prevent double clicks
          const element = e.currentTarget as HTMLInputElement;
          element.disabled = true;
          try {
            _onClick(e);
          } finally {
            element.disabled = false;
          }
        };
      }

      if (asLink) {
        return (
          <a
            onClick={onClick}
            key={key}
            className={classNames(
              baseClassName,
              'u-small-padding-left',
              'u-btn-af-height',
              'modal-button-as-link',
              {faded}
            )}
            data-method={button.dataMethod}
            href={button.href}
            {...getTestProps(button.dataTestAttrs)}
          >
            {text}
          </a>
        );
      }

      const legacyButtonStyles = {
        btn: true,
        'btn-af-default': style === 'default',
        'btn-af-primary': style === 'primary',
        'btn-af-success': style === 'success',
        'btn-af-danger': style === 'danger',
      };
      const redesignButtonStyles = {
        button: true,
        'button--medium': true,
        'button--primary': style === ButtonStyle.PRIMARY,
        'button--secondary': style === ButtonStyle.SECONDARY,
        'button--tertiaryBlue': style === ButtonStyle.TERTIARY_BLUE,
        'button--tertiaryGray': style === ButtonStyle.TERTIARY_GRAY,
        'button--negative': style === ButtonStyle.NEGATIVE,
        'button--warning': style === ButtonStyle.WARNING,
      };

      if (tooltipTitle) {
        return (
          <Tooltip key={`${key}-tooltip`} text={tooltipTitle}>
            <button
              key={key}
              // eslint-disable-next-line react/button-has-type
              type={type}
              onClick={(e) => {
                // We don't pass disabled to this button because it prevents pointer events, which we need in
                // order to get the prop.
                if (!disabled) {
                  onClick?.(e);
                }
              }}
              // Exclude disabled and handle styling with className, since it prohibits tooltips from showing up.
              // We still check in the onClick handler whether the button is disabled.
              className={classNames(
                baseClassName,
                this.props.enableRedesign ? redesignButtonStyles : legacyButtonStyles,
                {textButtonDisabled: disabled}
              )}
              {...getTestProps(button.dataTestAttrs)}
            >
              {text}
            </button>
          </Tooltip>
        );
      }

      return (
        <button
          key={key}
          // eslint-disable-next-line react/button-has-type
          type={type}
          onClick={onClick}
          disabled={disabled}
          className={classNames(
            baseClassName,
            this.props.enableRedesign ? redesignButtonStyles : legacyButtonStyles
          )}
          {...getTestProps(button.dataTestAttrs)}
        >
          {text}
        </button>
      );
    });
  }

  private renderModal(): JSX.Element {
    const titleId = `modal-title-${this.state.cid}`;
    const descriptionId = `modal-description-${this.state.cid}`;
    return (
      <div
        role="dialog"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={classNames(
          'modal',
          this.props.className,
          this.props.zIndex === 1060 && css(styles.modal1060),
          {
            'modal--redesign': this.props.enableRedesign,
            'modal--scrollable': this.props.scrollable,
          }
        )}
        onKeyDown={this.handleOnKeyDown}
        ref={this.modalRef}
        id={this.state.cid}
        data-modal-cid={this.state.cid}
        {...getTestProps(this.props.dataTestAttrs)}
      >
        <div
          className={classNames({
            'modal-dialog': true,
            'modal-xlg': this.props.extraLarge,
            'modal-fullWidth': this.props.fullWidth,
            'modal-fullHeight': this.props.fullHeight,
            'modal-expandHeight': this.props.expandHeight,
            'modal-lg': this.props.large,
            'modal-mdlg': this.props.mediumLarge,
            'modal-md': this.props.medium,
            'modal-sm': this.props.small,
          })}
        >
          <div className={classNames('modal-content', {[css(styles.flexContent)]: this.props.flexContent})}>
            {!this.props.suppressHeader && (
              <div
                className={classNames(
                  'modal-header',
                  css(styles.modalHeader, !this.props.showHeaderBorder && styles.modalHeaderNoBorder)
                )}
                ref={this.modalHeadingRef}
                tabIndex={-1}
              >
                <h4 className="modal-title" id={titleId}>
                  {this.props.titleIcon && (
                    <span className={classNames('modal-header-icon', css(styles.titleIcon))}>
                      <BenchlingIcon icon={this.props.titleIcon} />
                    </span>
                  )}
                  {this.props.title}
                </h4>
                <div>
                  {this.props.additionalHeaderIcons && this.props.additionalHeaderIcons.map((icon) => icon)}
                  {this.props.closeable && (
                    <BenchlingIcon
                      className={classNames('modal-header-icon', css(styles.modalClose))}
                      icon="close"
                      altText="Close"
                      data-dismiss="modal"
                    />
                  )}
                </div>
              </div>
            )}
            <div
              id={descriptionId}
              className={classNames(
                'modal-body',
                (this.props.fullHeight || this.props.expandHeight) && 'scrollbar-override',
                !this.props.bodyPadded && 'modal-body--no-padding',
                css(getBodyBackgroundStyle(this.props.modalBodyBgColor)),
                {[css(styles.flexBody)]: this.props.flexBody},
                {[css(styles.centerBody)]: this.props.centerBody}
              )}
              {...getTestProps({component: 'Modal', element: 'body'})}
            >
              {this.props.children}
            </div>
            {(this.props.buttons || this.props.footer) && (
              <div
                className={classNames(
                  'modal-footer',
                  css(!this.props.showFooterBorder && styles.modalFooterNoBorder)
                )}
                {...getTestProps({component: 'Modal', element: 'footer'})}
              >
                {this.props.bannerProps && (
                  <div className={classNames('u-small-spacing-bottom', css(styles.modalBanner))}>
                    <InlineBanner {...this.props.bannerProps} />
                  </div>
                )}
                {this.props.footer && (
                  <span className={this.props.footerPullRight ? 'u-spacing-right' : 'pull-left'}>
                    {this.props.footer}
                  </span>
                )}
                {this.props.wrapInForm ? (
                  this.renderModalFooterButtons()
                ) : (
                  // If whole modal isn't wrapped in <form>, then wrap only the footer buttons in <form>.
                  <form onSubmit={this.handleSubmit}>{this.renderModalFooterButtons()}</form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  override render(): JSX.Element {
    assertAndContinue(
      !this.props.onRequestSubmit || (this.props.buttons != null && this.getSubmitButtons().length > 0),
      'Modal should have at least one submit button, or form submission may not work.'
    );
    assert(
      !this.props.fullWidth || !this.props.extraLarge,
      'Modal should not provide both fullWidth and extraLarge size props because extraLarge overrides fullWidth'
    );

    if (this.props.wrapInForm) {
      return <form onSubmit={this.handleSubmit}>{this.renderModal()}</form>;
    }
    return this.renderModal();
  }
}

const styles = StyleSheet.create({
  modal1060: {
    zIndex: 1060,
  },
  modalClose: {
    marginRight: 0,
  },
  modalHeader: {
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
  },
  modalHeaderNoBorder: {
    borderBottom: 'none',
  },
  modalFooterNoBorder: {
    borderTop: 'none',
  },
  modalBanner: {
    textAlign: 'left',
  },
  flexBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  flexContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  centerBody: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleIcon: {
    marginLeft: 0,
  },
  whiteBg: {
    background: WHITE,
  },
  grayLighterBg: {
    background: GRAY_LIGHTER,
  },
  // The default of .modal-content in modals.less is a transparent background
  transparentBg: {},
  textButtonDisabled: {
    color: GRAY_DARK,
    ...hoverAndActiveStyles({
      color: GRAY_DARK,
    }),
  },
});

/**
 * @returns the DOM node of the currently open modal, if one exists.
 */
export function findOpenModal(): HTMLElement | null {
  const query = $('.modal').filter(':visible');
  return query.length > 0 ? query[0] : null;
}

export default React.forwardRef((props: Omit<ModalProps, 'modalRef'>, ref: RefObject<HTMLDivElement>) => (
  <Modal modalRef={ref} {...props} />
));
